/**
 * ============================================================================
 *  File        : frontend/src/lib/settlementShare.ts
 *  Module      : lib
 *  Purpose     : 三方分配 / 希望調整 的純函數（client-side 校驗與預覽用）。
 *                後端 settlement_calculation 為最終權威；此處僅做即時提示與
 *                無 P2 run 時的預覽試算，邏輯與後端對齊。
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  ⚠️ 此模組僅處理「整數単価」層級的 K/P/Q/R（業務上 P/Q/R 為整數単価），
 *  不做税込金額累積；金額累積一律由後端 rust_decimal 處理（CLAUDE.md §2）。
 */

/** 希望調整下限（business-rules §4：K = J − M ≥ 5，需有可分配利潤）。 */
export const PRICE_ADJUST_GAP_MIN = 5
/** 希望調整上限（business-rules §4：J − M ≤ 20）。 */
export const PRICE_ADJUST_GAP_MAX = 20

/**
 * 希望調整 J − M ∈ [5,20] 校驗（J = 落札単価, M = メンバー調整後単価）。
 * 後端僅硬性強制下限 ≥ 5；前端做完整 [5,20]。
 */
export function gapInRange(auctionPrice: number, adjustedPrice: number): boolean {
  const gap = auctionPrice - adjustedPrice
  return gap >= PRICE_ADJUST_GAP_MIN && gap <= PRICE_ADJUST_GAP_MAX
}

/** 三方分配嚴格相等校驗（invariant #2：P + Q + R = K）。 */
export function validateThreeWaySplit(p: number, q: number, r: number, k: number): boolean {
  return p + q + r === k
}

/**
 * 預設 20/40/40 分配（與後端 settlement_calculation::default_share 同演算法）：
 *   P = round_half_away_from_zero(K × 0.2)
 *   Q = round_half_away_from_zero(K × 0.4)
 *   R = K − P − Q          （扣減保 P + Q + R = K）
 * K 在業務上為整數単価。
 */
export function defaultShare(k: number): { p: number; q: number; r: number } {
  const roundHalfUp = (x: number): number => Math.sign(x) * Math.round(Math.abs(x))
  const p = roundHalfUp(k * 0.2)
  const q = roundHalfUp(k * 0.4)
  const r = k - p - q
  return { p, q, r }
}
